import type {
	OtpRequestMethods,
	OtpValidateMethods,
	OtpValidationResponse,
	TotpConfig,
	UserRole,
	VerifyEmailResponse
} from "@/types/auth"
import type { DateString } from "@/types/common"
import type { Doctor } from "@/types/doctor"
import type { UserDevice } from "@/types/user"
import { HttpClient, HUB_ROOT } from "@/api/httpClient"

export interface ResetPasswordPayload {
	email: string
	token: string
	password: string
	confirm_password: string
}

export interface UpdatePasswordPayload {
	old_password: string
	new_password: string
	confirm_new_password: string
}

export interface LoginPayload {
	email: string
	password: string
	fingerprint?: string
	otp?: string
	otp_method?: OtpValidateMethods
	remember_device?: 0 | 1
	device_name?: string
}

export interface RegisterPayload {
	email: string
	password: string
	confirm_password: string
	role: string
	name: string
	surname: string
	phone: string
	phone_token: string
	flag_marketing_direct: 1 | 0
	flag_marketing_indirect: 1 | 0
	flag_terms_and_conditions: 1 | 0
	flag_unfair_clauses: 1 | 0
	/** @deprecated  */
	// flag_data_share: 1 | 0
	/** @deprecated  */
	// flag_profiling: 1 | 0
	license_number: string
	license_country: string
	document_front?: File
	document_back?: File
	/** YYYY-MM-DD */
	birthdate: DateString
	picture?: File
	lang: string
	timezone: string
}

export interface DocumentsPayload {
	document_front: File
	document_back: File
}

export interface OtpRequestPayload {
	method?: OtpRequestMethods
	source?: string
	action: "REGISTRATION" | "LOGIN" | "UPDATE_PHONE" | "UPDATE_EMAIL" | "GENERIC_AUTH"
}

export interface OtpValidatePayload {
	method: OtpValidateMethods
	otp: string
}

export interface VerifyPhonePayload {
	role: UserRole
	phone: string
	phone_token: string
}

export interface VerifyEmailPayload {
	role: UserRole
	email: string
}

export interface ProfilePayload {
	email: string
	lang: string
	name: string
	surname: string
	phone: string
	phone_token: string
	birthdate: string
	flag_marketing_direct: 1 | 0
	flag_marketing_indirect: 1 | 0
	flag_terms_and_conditions: 1 | 0
	flag_unfair_clauses: 1 | 0
	timezone: string
	avg_visit_duration: number
}

export interface SetTotpPayload {
	secret: string
	otp: string
}

export interface DataExportAskResponse {
	message?: string
}

export default {
	login(payload: LoginPayload) {
		return HttpClient.post<Doctor>(`${HUB_ROOT}/login/web-doctor`, payload)
	},
	requestOtp(payload: OtpRequestPayload) {
		return HttpClient.post(`${HUB_ROOT}/users/otp/request`, payload)
	},
	validateOtp(payload: OtpValidatePayload) {
		return HttpClient.post<OtpValidationResponse>(`${HUB_ROOT}/users/otp/validate`, payload)
	},
	verifyPhone(payload: VerifyPhonePayload) {
		return HttpClient.post<Doctor>(`${HUB_ROOT}/users/registration/verify/phone`, payload)
	},
	verifyEmail(payload: VerifyEmailPayload) {
		return HttpClient.post<never | VerifyEmailResponse>(`${HUB_ROOT}/users/registration/verify/email`, payload)
	},
	getOtpMethods() {
		return HttpClient.get<OtpValidateMethods[]>(`${HUB_ROOT}/users/otp/methods`)
	},
	getTotpConfig() {
		return HttpClient.get<TotpConfig>(`${HUB_ROOT}/users/totp`)
	},
	setTotp(payload: SetTotpPayload) {
		return HttpClient.post(`${HUB_ROOT}/users/totp`, payload)
	},
	resetTotp(tempAuthToken: string) {
		return HttpClient.patch(`${HUB_ROOT}/users/totp/disable`, { temp_auth_token: tempAuthToken })
	},
	register(payload: RegisterPayload) {
		return HttpClient.postForm<Doctor>(`${HUB_ROOT}/users`, payload)
	},
	forgotPassword(email: string) {
		return HttpClient.post(`${HUB_ROOT}/password`, { email })
	},
	resetPassword(payload: ResetPasswordPayload) {
		return HttpClient.put(`${HUB_ROOT}/password`, payload)
	},
	updatePassword(payload: UpdatePasswordPayload, temp_auth_token: string) {
		return HttpClient.put(`${HUB_ROOT}/users/password`, { ...payload, temp_auth_token })
	},
	getProfile() {
		return HttpClient.get<Doctor>(`${HUB_ROOT}/users`)
	},
	updateProfile(payload: Partial<ProfilePayload>) {
		return HttpClient.put<Doctor>(`${HUB_ROOT}/users`, payload)
	},
	deleteAccount(temp_auth_token: string) {
		return HttpClient.delete(`${HUB_ROOT}/users`, { params: { temp_auth_token } })
	},
	askDataExport() {
		return HttpClient.get<DataExportAskResponse>(`${HUB_ROOT}/users/data-export`)
	},
	downloadDataExport(entityHash: string, password: string) {
		return HttpClient.get<Blob>(`${HUB_ROOT}/ext/downloader/${entityHash}`, {
			params: { password },
			responseType: "blob"
		})
	},
	getDevices() {
		return HttpClient.get<UserDevice[]>(`${HUB_ROOT}/users/devices`)
	},
	deleteDevice(deviceId: number) {
		return HttpClient.delete(`${HUB_ROOT}/users/devices/${deviceId}`)
	},
	uploadDocuments(payload: DocumentsPayload) {
		return HttpClient.postForm<Doctor>(`${HUB_ROOT}/users/doctors/documents`, payload)
	},
	updatePicture(picture: Blob | null) {
		return HttpClient.postForm(`${HUB_ROOT}/users/picture`, { picture })
	},
	updateEmail(otp: string, temp_auth_token: string) {
		return HttpClient.put(`${HUB_ROOT}/users/email`, { otp, temp_auth_token })
	},
	updatePhone(otp: string, temp_auth_token: string) {
		return HttpClient.put(`${HUB_ROOT}/users/phone`, { otp, temp_auth_token })
	}
}
